import { Controller } from '@hotwired/stimulus'

// Shows each `flaggable` target when any of its flags is enabled. A flag joined
// with `+` (e.g. `individual+income_plan`) needs every part enabled.
//
// The enabled flags are rebuilt from every `choice` and `checkbox` target on
// each change, so a container holding several Advanced Planning submissions
// shows the union of what each one asks for. A checkbox `input-reveal` has
// hidden (e.g. reports on a card switched to 401(k)) doesn't count.
export default class AttachmentNotesController extends Controller<HTMLElement> {
  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly choiceTargets: HTMLElement[]
  declare readonly flaggableTargets: HTMLElement[]

  static targets = ['checkbox', 'choice', 'flaggable']

  // Lifecycle methods

  connect() {
    this.element.addEventListener('input', this.#handleInput)
    // `input-reveal` hides sections on `change`, after `input` has fired
    this.element.addEventListener('change', this.#handleInput)
    this.#updateFlaggableVisibility()
    this.dispatch('connect')
  }

  disconnect() {
    this.element.removeEventListener('input', this.#handleInput)
    this.element.removeEventListener('change', this.#handleInput)
  }

  checkboxTargetConnected() {
    this.#updateFlaggableVisibility()
  }

  checkboxTargetDisconnected() {
    this.#updateFlaggableVisibility()
  }

  choiceTargetConnected() {
    this.#updateFlaggableVisibility()
  }

  choiceTargetDisconnected() {
    this.#updateFlaggableVisibility()
  }

  flaggableTargetConnected() {
    this.#updateFlaggableVisibility()
  }

  // Private methods

  #enabledFlags() {
    const flags = new Set<string>()

    this.choiceTargets.forEach(choice => {
      if (choice.dataset.attachmentNotesFlag) flags.add(choice.dataset.attachmentNotesFlag)
    })

    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.checked && checkbox.dataset.attachmentNotesFlag && !this.#isRevealHidden(checkbox)) {
        flags.add(checkbox.dataset.attachmentNotesFlag)
      }
    })

    return flags
  }

  #handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement
    const choice = this.choiceTargets.find(choice => choice.contains(input))

    if (choice) choice.dataset.attachmentNotesFlag = input.value
    if (choice || this.checkboxTargets.includes(input)) this.#updateFlaggableVisibility()
  }

  #isRevealHidden(element: HTMLElement) {
    return element.closest('[data-input-reveal-target="revealable"]')?.classList.contains('hidden') ?? false
  }

  #updateFlaggableVisibility() {
    const enabledFlags = this.#enabledFlags()

    this.flaggableTargets.forEach(flaggable => {
      const flags = flaggable.dataset.attachmentNotesFlaggableFlags?.split(' ') || []

      flaggable.hidden = !flags.some(flag => flag.split('+').every(part => enabledFlags.has(part)))
    })
  }
}
